import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import type { ColumnsType, TablePaginationConfig, TableProps } from "antd/es/table";
import { ApartmentOutlined, CopyOutlined, EyeOutlined, GlobalOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import * as api from "../api";
import type {
  ClusterItem,
  CreateClusterBindingRequest,
  CreateClusterRequest,
  CreateVirtualHostRequest,
} from "../api";

interface ClusterManagementProps {
  open: boolean;
  onCancel: () => void;
  onClustersChanged: () => void;
}

const getClusterEnv = (record: ClusterItem) =>
  record.cluster_env ?? record.clusterEnv ?? "";

const ClusterManagement: React.FC<ClusterManagementProps> = ({
  open,
  onCancel,
  onClustersChanged,
}) => {
  const [form] = Form.useForm<CreateClusterRequest>();
  const [bindingForm] = Form.useForm<CreateClusterBindingRequest>();
  const [hostForm] = Form.useForm<CreateVirtualHostRequest>();
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [bindingCreating, setBindingCreating] = useState(false);
  const [hostCreating, setHostCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [bindingOpen, setBindingOpen] = useState(false);
  const [hostOpen, setHostOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<ClusterItem | null>(null);
  const [data, setData] = useState<ClusterItem[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchClusters = useCallback(async (pageNum = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const res = await api.listClusters({
        page_num: pageNum,
        page_size: pageSize,
      });
      setData(res.data.items || []);
      setPagination({
        current: Math.max(1, res.data.page_num || pageNum),
        pageSize: res.data.page_size || pageSize,
        total: res.data.total_count || 0,
      });
    } catch (error) {
      message.error("获取集群列表失败");
      setData([]);
      setPagination((prev) => ({ ...prev, current: 1, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchClusters(1, pagination.pageSize || 10);
    }
  }, [fetchClusters, open, pagination.pageSize]);

  const handleTableChange: TableProps<ClusterItem>["onChange"] = (next) => {
    fetchClusters(next.current || 1, next.pageSize || 10);
  };

  const handleShowDetail = async (cluster: ClusterItem) => {
    if (!cluster.cluster_id) {
      message.warning("当前集群缺少 cluster_id，无法查询详情");
      return;
    }
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await api.detailCluster(cluster.cluster_id);
      setDetail(res.data);
    } catch (error) {
      message.error("获取集群详情失败");
      setDetail(cluster);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreate = async () => {
    const values = await form.validateFields();
    setCreating(true);
    try {
      await api.createCluster(values);
      message.success("集群创建成功");
      setCreateOpen(false);
      form.resetFields();
      await fetchClusters(1, pagination.pageSize || 10);
      onClustersChanged();
    } catch (error) {
      message.error("集群创建失败");
    } finally {
      setCreating(false);
    }
  };

  const handleOpenBinding = (cluster?: ClusterItem) => {
    bindingForm.setFieldsValue({
      cluster_id: cluster?.cluster_id || "",
      cluster_env: cluster ? getClusterEnv(cluster) : "",
    });
    setBindingOpen(true);
  };

  const handleCreateBinding = async () => {
    const values = await bindingForm.validateFields();
    setBindingCreating(true);
    try {
      await api.createClusterBinding(values);
      message.success("业务环境绑定成功");
      setBindingOpen(false);
      bindingForm.resetFields();
      onClustersChanged();
    } catch (error) {
      message.error("业务环境绑定失败");
    } finally {
      setBindingCreating(false);
    }
  };

  const handleCreateHost = async () => {
    const values = await hostForm.validateFields();
    setHostCreating(true);
    try {
      await api.createVirtualHost({
        ...values,
        remark: values.remark || "created from traffic replay frontend",
      });
      message.success("域名新增成功");
      setHostOpen(false);
      hostForm.resetFields();
      onClustersChanged();
    } catch (error) {
      message.error("域名新增失败");
    } finally {
      setHostCreating(false);
    }
  };

  const columns: ColumnsType<ClusterItem> = [
    {
      title: "集群 ID",
      dataIndex: "cluster_id",
      width: 220,
      render: (clusterId: string) => (
        <Space size={4}>
          <Tooltip title={clusterId || "未返回"}>
            <span style={{ display: "inline-block", maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {clusterId || "-"}
            </span>
          </Tooltip>
          {clusterId && (
            <Tooltip title="复制集群 ID">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(clusterId);
                  message.success("已复制集群 ID");
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "名称",
      dataIndex: "cluster_name",
      width: 180,
      render: (name: string) => name || "-",
    },
    {
      title: "类型",
      dataIndex: "cluster_type",
      width: 120,
      render: (type: string) => <Tag color="blue">{type || "未知"}</Tag>,
    },
    {
      title: "环境",
      width: 120,
      render: (_: unknown, record: ClusterItem) => (
        <Tag color="green">{getClusterEnv(record) || "未知"}</Tag>
      ),
    },
    {
      title: "平台",
      dataIndex: "platform",
      width: 120,
      render: (platform: string) => platform || "-",
    },
    {
      title: "备注",
      dataIndex: "remark",
      ellipsis: true,
      render: (remark: string) => remark || "-",
    },
    {
      title: "操作",
      key: "action",
      width: 140,
      align: "center",
      render: (_: unknown, record: ClusterItem) => (
        <Space size={4}>
          <Tooltip title="绑定业务环境">
            <Button
              size="small"
              icon={<ApartmentOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenBinding(record);
              }}
            />
          </Tooltip>
          <Tooltip title="查看详情">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleShowDetail(record);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="集群管理"
        open={open}
        onCancel={onCancel}
        footer={null}
        width="78%"
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
            >
              注册集群
            </Button>
            <Button
              icon={<ApartmentOutlined />}
              onClick={() => handleOpenBinding()}
            >
              绑定业务环境
            </Button>
            <Button
              icon={<GlobalOutlined />}
              onClick={() => setHostOpen(true)}
            >
              新增域名
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchClusters(pagination.current || 1, pagination.pageSize || 10)}
            >
              刷新
            </Button>
          </Space>
          <Table<ClusterItem>
            rowKey={(record) => record.cluster_id || `${record.cluster_name}-${getClusterEnv(record)}`}
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            size="small"
            scroll={{ x: 980 }}
          />
        </Space>
      </Modal>

      <Modal
        title="绑定业务环境"
        open={bindingOpen}
        onCancel={() => setBindingOpen(false)}
        onOk={handleCreateBinding}
        okText="提交"
        confirmLoading={bindingCreating}
        destroyOnClose
        width={560}
      >
        <Form<CreateClusterBindingRequest>
          form={bindingForm}
          layout="vertical"
          preserve={false}
        >
          <Form.Item
            label="集群 ID"
            name="cluster_id"
            rules={[{ required: true, message: "请输入集群 ID" }]}
          >
            <Input placeholder="选择集群时自动带入，也可手动填写" />
          </Form.Item>
          <Form.Item
            label="业务线"
            name="business"
            rules={[{ required: true, message: "请输入业务线" }]}
          >
            <Input placeholder="例如 litatom" />
          </Form.Item>
          <Form.Item
            label="环境"
            name="cluster_env"
            rules={[{ required: true, message: "请输入环境" }]}
          >
            <Input placeholder="例如 test / hbl.dev / yw" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="新增业务域名"
        open={hostOpen}
        onCancel={() => setHostOpen(false)}
        onOk={handleCreateHost}
        okText="提交"
        confirmLoading={hostCreating}
        destroyOnClose
        width={560}
      >
        <Form<CreateVirtualHostRequest>
          form={hostForm}
          layout="vertical"
          preserve={false}
        >
          <Form.Item
            label="业务线"
            name="business"
            rules={[{ required: true, message: "请输入业务线" }]}
          >
            <Input placeholder="例如 litatom" />
          </Form.Item>
          <Form.Item
            label="环境"
            name="cluster_env"
            rules={[{ required: true, message: "请输入环境" }]}
          >
            <Input placeholder="例如 test / hbl.dev / yw" />
          </Form.Item>
          <Form.Item
            label="域名"
            name="host"
            rules={[{ required: true, message: "请输入域名" }]}
          >
            <Input placeholder="例如 api.example.com" />
          </Form.Item>
          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={3} placeholder="可选，默认写入前端创建说明" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="注册集群"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        okText="提交"
        confirmLoading={creating}
        destroyOnClose
        width={680}
      >
        <Form<CreateClusterRequest>
          form={form}
          layout="vertical"
          preserve={false}
        >
          <Form.Item
            label="集群名称"
            name="cluster_name"
            rules={[{ required: true, message: "请输入集群名称" }]}
          >
            <Input placeholder="例如 prod-hk-ack" />
          </Form.Item>
          <Form.Item
            label="集群类型"
            name="cluster_type"
            rules={[{ required: true, message: "请输入集群类型" }]}
          >
            <Input placeholder="例如 ack / tke / k8s" />
          </Form.Item>
          <Form.Item
            label="平台"
            name="platform"
            rules={[{ required: true, message: "请输入平台" }]}
          >
            <Input placeholder="例如 aliyun / tencent / self-hosted" />
          </Form.Item>
          <Form.Item
            label="环境"
            name="cluster_env"
            rules={[{ required: true, message: "请输入环境" }]}
          >
            <Input placeholder="例如 prod / st1 / itr" />
          </Form.Item>
          <Form.Item
            label="kubeconfig"
            name="kube_content"
            rules={[{ required: true, min: 15, message: "请输入有效 kubeconfig" }]}
          >
            <Input.TextArea rows={8} placeholder="粘贴 kubeconfig 内容" />
          </Form.Item>
          <Form.Item
            label="备注"
            name="remark"
            rules={[{ required: true, min: 5, message: "请输入至少 5 个字符的备注" }]}
          >
            <Input.TextArea rows={3} placeholder="说明用途、负责人或接入范围" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="集群详情"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={760}
        destroyOnClose
      >
        <Table
          rowKey="key"
          loading={detailLoading}
          pagination={false}
          size="small"
          columns={[
            { title: "字段", dataIndex: "label", width: 140 },
            {
              title: "值",
              dataIndex: "value",
              render: (value: string) => value || "-",
            },
          ]}
          dataSource={[
            { key: "cluster_id", label: "集群 ID", value: detail?.cluster_id },
            { key: "cluster_name", label: "集群名称", value: detail?.cluster_name },
            { key: "cluster_type", label: "集群类型", value: detail?.cluster_type },
            { key: "cluster_env", label: "环境", value: detail ? getClusterEnv(detail) : "" },
            { key: "platform", label: "平台", value: detail?.platform },
            { key: "remark", label: "备注", value: detail?.remark },
          ]}
        />
      </Modal>
    </>
  );
};

export default ClusterManagement;
